import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig, onDocumentReady } from '@theme/utilities';

let initialized = false;
let mirrorSyncInFlight = false;
let mirrorEngravingInFlight = false;
let mirrorCheckoutPromptInFlight = false;
let checkoutBusyLockCount = 0;
let packagingUpdateInFlight = false;
const CART_PACKAGING_GLOBAL_INIT_KEY = '__cartPackagingInitialized';

/**
 * @returns {{ variantId: string, thresholdCents: number, engravingThresholdCents: number } | null}
 */
function getMirrorSyncConfig() {
  const el = document.querySelector('[data-free-mirror-sync], [data-free-mirror-gift]');
  if (!(el instanceof HTMLElement)) return null;

  const variantId = el.dataset.mirrorVariantId?.trim();
  const thresholdCents = Number(el.dataset.mirrorThresholdCents || 0);
  const engravingThresholdCents = Number(el.dataset.mirrorEngravingThresholdCents || 0);
  if (!variantId || thresholdCents <= 0) return null;

  return { variantId, thresholdCents, engravingThresholdCents };
}

/**
 * @returns {Record<string, string>}
 */
function getSectionsPayload() {
  const sectionIds = getSectionIds();
  if (!sectionIds.length) return {};
  return {
    sections: sectionIds.join(','),
    sections_url: window.location.pathname,
  };
}

/**
 * @returns {HTMLElement | null}
 */
function getMirrorGiftContainer() {
  const container = document.querySelector('[data-free-mirror-gift]');
  return container instanceof HTMLElement ? container : null;
}

/**
 * @returns {HTMLInputElement | null}
 */
function getMirrorEngravingInput() {
  const input = document.querySelector('[data-free-mirror-engraving-input]');
  return input instanceof HTMLInputElement ? input : null;
}

/**
 * @param {boolean} engravingEligible
 * @param {boolean} clearValueWhenLocked
 */
function applyMirrorEngravingInputState(engravingEligible, clearValueWhenLocked = false) {
  const mirrorContainer = getMirrorGiftContainer();
  const engravingInput = getMirrorEngravingInput();
  if (!(mirrorContainer instanceof HTMLElement) || !(engravingInput instanceof HTMLInputElement)) return;

  engravingInput.disabled = !engravingEligible;
  engravingInput.placeholder = engravingEligible
    ? engravingInput.dataset.placeholderUnlocked || 'Add engraving'
    : engravingInput.dataset.placeholderLocked || engravingInput.placeholder;

  if (!engravingEligible && clearValueWhenLocked) {
    engravingInput.value = '';
    mirrorContainer.dataset.mirrorEngravingSynced = '';
  }
}

/**
 * @param {HTMLElement} checkoutButton
 */
function submitCheckoutFromButton(checkoutButton) {
  const checkoutFormId = checkoutButton.getAttribute('form');
  let checkoutForm = null;
  if (checkoutFormId) {
    const formNode = document.getElementById(checkoutFormId);
    if (formNode instanceof HTMLFormElement) checkoutForm = formNode;
  }
  if (!checkoutForm && checkoutButton instanceof HTMLButtonElement && checkoutButton.form instanceof HTMLFormElement) {
    checkoutForm = checkoutButton.form;
  }
  if (!checkoutForm) return;

  if (checkoutButton instanceof HTMLButtonElement && typeof checkoutForm.requestSubmit === 'function') {
    if (checkoutButton.disabled) {
      checkoutForm.requestSubmit();
    } else {
      checkoutForm.requestSubmit(checkoutButton);
    }
  } else {
    checkoutForm.submit();
  }
}

/**
 * @param {HTMLElement} checkoutButton
 * @param {boolean} isLoading
 */
function setCheckoutLoading(checkoutButton, isLoading) {
  const button = checkoutButton instanceof HTMLButtonElement || checkoutButton instanceof HTMLInputElement ? checkoutButton : null;
  if (!button) return;

  if (isLoading) {
    button.dataset.mirrorCheckoutWasDisabled = button.disabled ? 'true' : 'false';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    return;
  }

  const wasDisabled = button.dataset.mirrorCheckoutWasDisabled === 'true';
  button.disabled = wasDisabled;
  button.removeAttribute('aria-busy');
  button.classList.remove('is-loading');
  delete button.dataset.mirrorCheckoutWasDisabled;
}

function getCheckoutButtons() {
  return [...document.querySelectorAll('button[name="checkout"], input[name="checkout"]')].filter(
    (button) => button instanceof HTMLButtonElement || button instanceof HTMLInputElement
  );
}

/**
 * Global checkout lock used while engraving property mutates.
 *
 * @param {boolean} isLoading
 */
function setCheckoutBusy(isLoading) {
  if (isLoading) {
    checkoutBusyLockCount += 1;
    if (checkoutBusyLockCount !== 1) return;
  } else {
    checkoutBusyLockCount = Math.max(0, checkoutBusyLockCount - 1);
    if (checkoutBusyLockCount !== 0) return;
  }

  getCheckoutButtons().forEach((button) => {
    if (isLoading) {
      button.dataset.mirrorCheckoutBusyWasDisabled = button.disabled ? 'true' : 'false';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-loading');
      return;
    }

    const wasDisabled = button.dataset.mirrorCheckoutBusyWasDisabled === 'true';
    button.disabled = wasDisabled;
    button.removeAttribute('aria-busy');
    button.classList.remove('is-loading');
    delete button.dataset.mirrorCheckoutBusyWasDisabled;
  });
}

/**
 * @returns {string[]}
 */
function getSectionIds() {
  const sections = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      sections.add(component.dataset.sectionId);
    }
  });
  return [...sections];
}

/**
 * @param {any} resource
 * @returns {boolean}
 */
function isCartResource(resource) {
  return !!resource && Array.isArray(resource.items) && typeof resource.items_subtotal_price === 'number';
}

/**
 * @returns {Promise<any | null>}
 */
async function fetchCartSilently() {
  try {
    const response = await fetch('/cart.js');
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
}

/**
 * @param {any} cart
 * @param {string} variantId
 * @returns {Array<{line: number, quantity: number}>}
 */
function findLinesForVariant(cart, variantId) {
  if (!cart?.items || !variantId) return [];
  const lines = [];
  cart.items.forEach((item, index) => {
    if (`${item.variant_id}` === `${variantId}`) {
      lines.push({ line: index + 1, quantity: Number(item.quantity) || 0 });
    }
  });
  return lines;
}

/**
 * @param {object} payload
 * @returns {Promise<any>}
 */
async function postCartAdd(payload) {
  const response = await fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body: JSON.stringify(payload) }));
  const data = await response.json();
  if (!response.ok) {
    console.error('Mirror add failed', data);
    throw new Error(data?.description || 'Mirror add failed');
  }
  return data;
}

/**
 * @param {object} payload
 * @returns {Promise<any>}
 */
async function postCartChange(payload) {
  const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body: JSON.stringify(payload) }));
  const data = await response.json();
  if (!response.ok) {
    console.error('Mirror cart change failed', data);
    throw new Error('Mirror cart change failed');
  }
  return data;
}

/**
 * @param {any} parsedResponse
 * @param {string} source
 */
function dispatchCartUpdate(parsedResponse, source) {
  document.dispatchEvent(
    new CartUpdateEvent(parsedResponse, source, {
      itemCount: parsedResponse?.item_count ?? 0,
      source,
      sections: parsedResponse?.sections,
    })
  );
}

/**
 * @param {string} variantId
 * @returns {Promise<number | null>}
 */
async function findLineForVariant(variantId) {
  try {
    const response = await fetch('/cart.js');
    if (!response.ok) return null;
    const cart = await response.json();
    const index = cart.items?.findIndex((item) => `${item.variant_id}` === `${variantId}`);
    if (typeof index === 'number' && index >= 0) return index + 1;
    return null;
  } catch {
    return null;
  }
}

/**
 * @param {number} line
 * @param {number} quantity
 * @param {string} engravingText
 */
async function saveMirrorEngraving(line, quantity, engravingText) {
  const sections = getSectionIds();
  const payload = {
    line,
    quantity: Math.max(1, Number(quantity || 1)),
    properties: {
      'Mirror engraving': engravingText,
    },
    sections: sections.join(','),
    sections_url: window.location.pathname,
  };

  const parsedResponse = await postCartChange(payload);
  dispatchCartUpdate(parsedResponse, 'mirror-gift');
  return parsedResponse;
}

/**
 * @param {string} engravingText
 * @returns {Promise<boolean>}
 */
async function syncMirrorEngravingToCart(engravingText) {
  setCheckoutBusy(true);
  const mirrorContainer = getMirrorGiftContainer();
  if (!(mirrorContainer instanceof HTMLElement)) {
    setCheckoutBusy(false);
    return false;
  }

  try {
    if (mirrorContainer.dataset.mirrorInCart !== 'true') {
      await syncMirrorGift(undefined);
    }

    const line = await resolveMirrorLine(mirrorContainer);
    if (!line) return false;

    const quantity = Math.max(1, Number(mirrorContainer.dataset.mirrorQuantity || 1));
    await saveMirrorEngraving(line, quantity, engravingText);
    mirrorContainer.dataset.mirrorEngravingSynced = engravingText;
    return true;
  } finally {
    setCheckoutBusy(false);
  }
}

/**
 * @param {HTMLElement} mirrorContainer
 * @returns {Promise<number>}
 */
async function resolveMirrorLine(mirrorContainer) {
  const variantId = mirrorContainer.dataset.mirrorVariantId?.trim();
  if (!variantId) return 0;

  let line = Number(mirrorContainer.dataset.mirrorLine || 0);
  if (!line) {
    line = Number(await findLineForVariant(variantId) || 0);
  }
  return line;
}

/**
 * @param {Element | null} container
 * @param {Element | null} optionRow
 * @param {boolean} isLoading
 */
function setPackagingLoading(container, optionRow, isLoading) {
  if (container instanceof HTMLElement) {
    container.classList.toggle('cart-packaging-preference--updating', isLoading);
    container.toggleAttribute('aria-busy', isLoading);
  }

  if (optionRow instanceof HTMLElement) {
    optionRow.classList.toggle('cart-packaging-option--loading', isLoading);
    const input = optionRow.querySelector('input[data-packaging-option]');
    if (input instanceof HTMLInputElement) {
      input.setAttribute('aria-busy', String(isLoading));
    }
  }
}

/**
 * @param {Event} event
 */
async function handlePackagingChange(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.matches('input[data-packaging-option]')) return;
  if (!target.checked) return;

  const container = target.closest('[data-cart-packaging]');
  if (!(container instanceof HTMLElement)) return;

  const option = target.dataset.packagingOption;
  const variantId = container.dataset.giftPackagingVariantId?.trim();
  const optionRow = target.closest('.cart-packaging-option');

  if (packagingUpdateInFlight) return;
  packagingUpdateInFlight = true;
  setPackagingLoading(container, optionRow, true);

  const sections = getSectionIds();
  const payload = {
    sections: sections.join(','),
    sections_url: window.location.pathname,
  };

  try {
    let targetQuantity = null;

    if (option === 'gift') {
      if (!variantId) return;
      targetQuantity = 1;
    } else if (option === 'eco') {
      if (!variantId) return;
      targetQuantity = 0;
    } else {
      return;
    }

    const body = JSON.stringify({
      updates: {
        [variantId]: targetQuantity,
      },
      ...payload,
    });
    const response = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }));
    if (!response.ok) {
      throw new Error('Cart packaging update failed');
    }

    const parsedResponse = await response.json();
    document.dispatchEvent(
      new CartUpdateEvent(parsedResponse, 'cart-packaging', {
        itemCount: parsedResponse.item_count,
        source: 'cart-packaging',
        sections: parsedResponse.sections,
      })
    );
  } catch (error) {
    console.error(error);
    const giftInput = container.querySelector('input[data-packaging-option="gift"]');
    const ecoInput = container.querySelector('input[data-packaging-option="eco"]');
    if (option === 'gift' && ecoInput instanceof HTMLInputElement) {
      ecoInput.checked = true;
    } else if (option === 'eco' && giftInput instanceof HTMLInputElement) {
      giftInput.checked = true;
    }
  } finally {
    setPackagingLoading(container, optionRow, false);
    packagingUpdateInFlight = false;
  }
}

/**
 * Fetch cart, compare subtotal to threshold, add/remove/normalize free mirror line.
 *
 * @param {CustomEvent | undefined} event
 */
async function syncMirrorGift(event) {
  if (event?.detail?.data?.source === 'mirror-gift' || mirrorSyncInFlight) return;

  mirrorSyncInFlight = true;
  let engravingMutationLocked = false;
  try {
    const config = getMirrorSyncConfig();
    if (!config) return;

    const cart = await fetchCartSilently();
    if (!cart) return;

    const { variantId, thresholdCents, engravingThresholdCents } = config;
    const subtotalCents = Number(cart.items_subtotal_price || 0);
    const eligible = subtotalCents >= thresholdCents;
    const engravingEligible = engravingThresholdCents <= 0 || subtotalCents >= engravingThresholdCents;
    const matchingLines = findLinesForVariant(cart, variantId);
    const primaryLine = matchingLines[0];
    const primaryItem = primaryLine ? cart.items?.[primaryLine.line - 1] : null;
    const hasEngravingProperty = `${primaryItem?.properties?.['Mirror engraving'] ?? ''}`.trim() !== '';
    const needsClearEngraving =
      eligible &&
      !!primaryLine &&
      hasEngravingProperty &&
      engravingThresholdCents > 0 &&
      subtotalCents < engravingThresholdCents;

    const needsRemove = !eligible && matchingLines.length > 0;
    const needsAdd = eligible && matchingLines.length === 0;
    const needsNormalize =
      eligible &&
      matchingLines.length > 0 &&
      (matchingLines[0].quantity !== 1 || matchingLines.length > 1);

    applyMirrorEngravingInputState(engravingEligible, !engravingEligible);

    if (!needsRemove && !needsAdd && !needsNormalize && !needsClearEngraving) return;

    const payloadBase = getSectionsPayload();
    let parsedResponse = null;
    const mutatingEngraving = needsClearEngraving;
    if (mutatingEngraving) {
      setCheckoutBusy(true);
      engravingMutationLocked = true;
    }

    if (needsRemove) {
      for (const lineData of matchingLines) {
        parsedResponse = await postCartChange({
          line: lineData.line,
          quantity: 0,
          ...payloadBase,
        });
      }
    } else if (needsAdd) {
      parsedResponse = await postCartAdd({
        items: [{ id: Number(variantId), quantity: 1 }],
        ...payloadBase,
      });
    } else if (needsNormalize) {
      const [normalizedPrimaryLine, ...extraLines] = matchingLines;

      if (normalizedPrimaryLine.quantity !== 1 || needsClearEngraving) {
        parsedResponse = await postCartChange({
          line: normalizedPrimaryLine.line,
          quantity: 1,
          ...(needsClearEngraving ? { properties: {} } : {}),
          ...payloadBase,
        });
      }

      for (const lineData of extraLines) {
        parsedResponse = await postCartChange({
          line: lineData.line,
          quantity: 0,
          ...payloadBase,
        });
      }
    }

    if (!needsRemove && !needsAdd && !needsNormalize && needsClearEngraving && primaryLine) {
      parsedResponse = await postCartChange({
        line: primaryLine.line,
        quantity: Math.max(1, Number(primaryLine.quantity || 1)),
        properties: {},
        ...payloadBase,
      });
    }

    if (parsedResponse) {
      dispatchCartUpdate(parsedResponse, 'mirror-gift');
    }
  } catch (error) {
    console.error(error);
  } finally {
    if (engravingMutationLocked) {
      setCheckoutBusy(false);
    }
    mirrorSyncInFlight = false;
  }
}

/**
 * @param {Event} event
 */
async function handleMirrorEngravingBlur(event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.matches('[data-free-mirror-engraving-input]') || mirrorEngravingInFlight) return;

  const mirrorContainer = getMirrorGiftContainer();
  if (!(mirrorContainer instanceof HTMLElement)) return;

  if (mirrorContainer.dataset.mirrorEngravingEligible !== 'true') return;
  const syncedValue = (mirrorContainer.dataset.mirrorEngravingSynced || '').trim();
  const inputValue = target.value.trim();
  if (inputValue === syncedValue) return;

  try {
    mirrorEngravingInFlight = true;
    await syncMirrorEngravingToCart(inputValue);
  } catch (error) {
    console.error(error);
  } finally {
    mirrorEngravingInFlight = false;
  }
}

/**
 * @param {MouseEvent} event
 */
async function handleMirrorCheckoutPrompt(event) {
  const target = event.target;
  if (!(target instanceof Element) || mirrorCheckoutPromptInFlight) return;

  const checkoutButton = target.closest('button[name="checkout"], input[name="checkout"]');
  if (!(checkoutButton instanceof HTMLElement)) return;

  const mirrorContainer = getMirrorGiftContainer();
  if (!(mirrorContainer instanceof HTMLElement)) return;

  if (mirrorContainer.dataset.mirrorEngravingEligible !== 'true') return;

  const engravingInput = getMirrorEngravingInput();
  const syncedValue = (mirrorContainer.dataset.mirrorEngravingSynced || '').trim();
  const inputValue = engravingInput?.value.trim() || '';

  // If customer typed engraving and it is not synced yet, save then continue.
  if (inputValue && inputValue !== syncedValue) {
    event.preventDefault();
    event.stopPropagation();
    setCheckoutLoading(checkoutButton, true);

    try {
      mirrorCheckoutPromptInFlight = true;
      const saved = await syncMirrorEngravingToCart(inputValue);
      if (!saved) {
        submitCheckoutFromButton(checkoutButton);
        return;
      }
      submitCheckoutFromButton(checkoutButton);
    } catch (error) {
      console.error(error);
      submitCheckoutFromButton(checkoutButton);
    } finally {
      mirrorCheckoutPromptInFlight = false;
      setCheckoutLoading(checkoutButton, false);
    }
    return;
  }

  // If either input or cart already has engraving, continue checkout.
  if (inputValue || syncedValue) return;

  event.preventDefault();
  event.stopPropagation();
  setCheckoutLoading(checkoutButton, true);

  const variantId = mirrorContainer.dataset.mirrorVariantId?.trim();
  if (!variantId) {
    setCheckoutLoading(checkoutButton, false);
    submitCheckoutFromButton(checkoutButton);
    return;
  }

  try {
    mirrorCheckoutPromptInFlight = true;

    if (mirrorContainer.dataset.mirrorInCart !== 'true') {
      await syncMirrorGift(undefined);
    }

    const maxChars = Math.max(1, Number(mirrorContainer.dataset.mirrorEngravingMaxChars || 20));
    const promptText = window.prompt(
      `Add optional engraving to your free compact mirror before checkout.\n\nEnter a name or date (max ${maxChars} characters), or type "none" to continue without engraving.`,
      ''
    );
    if (promptText === null) return;

    const normalizedPromptText = promptText.trim();
    if (!normalizedPromptText || normalizedPromptText.toLowerCase() === 'none') {
      submitCheckoutFromButton(checkoutButton);
      return;
    }

    const engravingText = normalizedPromptText.slice(0, maxChars);
    const saved = await syncMirrorEngravingToCart(engravingText);
    if (!saved) {
      window.alert('We could not find your free mirror line item. Please try again.');
      return;
    }
    submitCheckoutFromButton(checkoutButton);
  } catch (error) {
    console.error(error);
    window.alert('We could not save your mirror engraving. Please try again or type "none" to continue.');
  } finally {
    mirrorCheckoutPromptInFlight = false;
    setCheckoutLoading(checkoutButton, false);
  }
}

export function initCartPackaging() {
  if (initialized) return;
  if (typeof window !== 'undefined' && window[CART_PACKAGING_GLOBAL_INIT_KEY]) return;
  initialized = true;
  if (typeof window !== 'undefined') {
    window[CART_PACKAGING_GLOBAL_INIT_KEY] = true;
  }
  document.addEventListener('change', handlePackagingChange);
  document.addEventListener('focusout', handleMirrorEngravingBlur);
  document.addEventListener('click', handleMirrorCheckoutPrompt, true);
  document.addEventListener(ThemeEvents.cartUpdate, syncMirrorGift);
  void syncMirrorGift(undefined);
}

initCartPackaging();
